import { Button } from "@mui/material";
import LinkButton from "controls/LinkButton";
import { ContentCopy, Robot } from "mdi-material-ui";
import React, { useState } from "react";

// Builds a copy-paste prompt that instructs an AI assistant to convert a user's
// own spreadsheet into the exact CSV format this importer expects.
export function buildCsvAiPrompt({ subject, headers, formatNotes = {}, exampleRow = [] }) {
  const noteLines = headers.map((h) => `- ${h}: ${formatNotes[h] ?? "required"}`).join("\n");
  return [
    `I have a spreadsheet of ${subject} data that I need to convert into a specific CSV format for upload.`,
    "",
    "Convert my data into a CSV with EXACTLY these columns, in this order:",
    headers.join(", "),
    "",
    "Column rules:",
    noteLines,
    "",
    "Output rules:",
    "- The first row must be the exact header row shown above.",
    "- One record per row, values comma-separated.",
    "- Output only the raw CSV — no explanation, commentary, or code fences.",
    "- Do not add, remove, or rename columns.",
    "- If a value is unknown, leave that cell empty but keep every column.",
    "",
    "Here is one correctly formatted example row:",
    exampleRow.join(","),
    "",
    "Here is my data to convert (either pasted below or in the attached file):",
    "[PASTE YOUR SPREADSHEET DATA HERE, OR ATTACH/UPLOAD YOUR FILE TO THIS CHAT]",
  ].join("\n");
}

export default function CsvAiPromptBox({ subject, headers, formatNotes, exampleRow }) {
  const [copied, setCopied] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const prompt = buildCsvAiPrompt({ subject, headers, formatNotes, exampleRow });

  const handleCopy = async () => {
    try {
      await navigator.clipboard?.writeText(prompt);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard may be unavailable (e.g. insecure context); ignore silently.
    }
  };

  return (
    <div style={{ border: "1px solid #e0e0e0", borderRadius: 4, padding: 12, background: "#fafafa" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Robot fontSize="small" style={{ color: "#1976d2" }} />
        <span style={{ fontSize: "0.9em", flex: 1, minWidth: 200 }}>
          You can copy this prompt into an external AI tool (like ChatGPT or Claude) to help you with the data format.
          <br />
          <b>Note:</b> Please review your organisation&rsquo;s AI usage & data privacy policies first and always check
          the data accuracy before uploading the CSV.
        </span>
        <Button size="small" variant="outlined" startIcon={<ContentCopy />} onClick={handleCopy}>
          {copied ? "Copied!" : "Copy prompt"}
        </Button>
      </div>
      <div style={{ marginTop: 6 }}>
        <LinkButton onClick={() => setExpanded((v) => !v)}>{expanded ? "Hide prompt" : "Preview prompt"}</LinkButton>
      </div>
      {expanded && (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: "0.78em",
            background: "#fff",
            border: "1px solid #eee",
            borderRadius: 4,
            padding: 8,
            marginTop: 6,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {prompt}
        </pre>
      )}
    </div>
  );
}
